import { Box, Divider, Stack, styled, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CustomActionIconLink } from "../../../components/CustomActionIconLink/CustomActionIconLink";
import { DataTable, ColumnDef } from "../../../components/DataTable/DataTable";
import { useParams, Link as RouterLink, Link } from "react-router-dom";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import {
  GetReportTemplatesPagintedResult,
  GetReportTemplatesResult,
  ReportTemplateQueryParams,
  ReportTemplateRemoveBodyParams,
  ReportTemplateTypeEnum,
} from "../../../services/api/types/reports/reports";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import {
  deleteReportTemplate,
  getAllReportTemplates,
} from "../../../services/api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { brand } from "../../../theme/themePrimitives";
import { Roles } from "../../../services/api/types/users/users";

export const Reports: React.FC = () => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [templatesResp, setTemplatesResp] =
    useState<GetReportTemplatesPagintedResult>();
  const [sort, setSort] = useState<{ orderBy: string; order: "asc" | "desc" }>({
    orderBy: "name",
    order: "asc",
  });
  const [selectedTemplates, setSelectedTemplates] = useState<
    GetReportTemplatesResult[]
  >([]);
  const [confirmDeletionModal, setConfirmDeletionModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const anySelected = selectedTemplates.length > 0;
  const { showToast } = useToast();
  const { auth } = useAuth();
  const isAdmin =
    (auth.user?.roles?.includes(Roles.MAIN_ADMIN) ||
      auth.user?.roles?.includes(Roles.PORTAL_ADMIN) ||
      auth.user?.roles.includes(Roles.SYSTEM_ADMIN) ||
      auth.user?.roles.includes(Roles.ADMIN)) ??
    false;
  const isReportViewer =
    auth.user?.roles?.includes(Roles.REPORT_VIEWER) ?? false;
  const { portalUid } = useParams();

  const base = `/portal-configuration/${portalUid}`;

  const previousPortalUid = useRef<string | undefined>(portalUid);

  const StyledLink = styled(RouterLink)(({ theme }) => ({
    color: "white",
    backgroundColor: brand[600],
    padding: theme.spacing(1, 2),
    textDecoration: "none",
    borderRadius: "12px",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: brand[700],
    },
    "&:focus-visible": {
      outline: "2px solid rgba(0,0,0,0.4)",
      outlineOffset: "2px",
    },
  }));

  // Clear everything when changing portal
  useEffect(() => {
    if (
      previousPortalUid.current !== undefined &&
      previousPortalUid.current !== portalUid
    ) {
      setSelectedTemplates([]);
    }

    // Update the ref after the comparison
    previousPortalUid.current = portalUid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid]);

  useEffect(() => {
    fetchReportTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rowsPerPage, page, sort.orderBy, sort.order, portalUid]);

  // Refresh data when page becomes visible and a report was recently run
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Check if a report was run (set by report pages)
        const reportRunTimestamp = localStorage.getItem("reportRunTimestamp");
        const lastRefreshTimestamp = localStorage.getItem("reportsLastRefresh");

        // Only refresh if a report was run after our last refresh
        if (
          reportRunTimestamp &&
          (!lastRefreshTimestamp || reportRunTimestamp > lastRefreshTimestamp)
        ) {
          fetchReportTemplates();

          // Update last refresh timestamp
          localStorage.setItem("reportsLastRefresh", Date.now().toString());
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page, sort.orderBy, sort.order, search, portalUid]);

  const fetchReportTemplates = async () => {
    if (!portalUid) return;
    setLoading(true);

    const URLParams: ReportTemplateQueryParams = {
      limit: rowsPerPage,
      offset: (page - 1) * rowsPerPage,
      orderBy: sort.orderBy,
      sort: sort.order,
    };

    if (search) {
      URLParams.search = search;
    }

    const params = new URLSearchParams(window.location.search);
    const urlSearch = params.get("search");
    if (urlSearch) {
      URLParams.search = urlSearch;
    }

    try {
      const response = await getAllReportTemplates(portalUid, URLParams);
      setTemplatesResp(response);

      // Merge selected templates with fetched templates to get latest data
      if (selectedTemplates.length > 0 && response.rows) {
        const fetchedTemplateMap = new Map<string, GetReportTemplatesResult>(
          response.rows.map((template: GetReportTemplatesResult) => [
            template.ReportTemplateUid,
            template,
          ]),
        );

        const updatedSelectedTemplates: GetReportTemplatesResult[] =
          selectedTemplates.map((selectedTemplate) => {
            const fetchedTemplate = fetchedTemplateMap.get(
              selectedTemplate.ReportTemplateUid,
            );
            // Use fetched template if available (has latest data), otherwise keep selected
            return fetchedTemplate || selectedTemplate;
          });

        setSelectedTemplates(updatedSelectedTemplates);
      }
    } catch (error) {
      console.log("ERROR: ", error);
      setTemplatesResp(undefined);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleSortChange = (orderBy: string, order: "asc" | "desc") => {
    setSort({ orderBy, order });
  };

  const handleDeleteSelectedTemplates = async () => {
    if (selectedTemplates.length === 0) return;

    try {
      if (auth.user) {
        const body: ReportTemplateRemoveBodyParams = {
          reportTemplateUids: selectedTemplates.map((t) => t.ReportTemplateUid),
        };

        await deleteReportTemplate(portalUid!, body);

        showToast("Successfully deleted report templates", "success");

        // Reset selection + close modal
        setSelectedTemplates([]);
        setConfirmDeletionModal(false);

        // Refresh table
        fetchReportTemplates();
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      showToast(error?.response?.data || "Failed to delete templates", "error");
    }
  };

  const handleSelectionChange = (
    updatedTemplates: GetReportTemplatesResult[],
  ) => {
    setSelectedTemplates(updatedTemplates);
  };

  const formatLastViewedField = (value?: string | null) => {
    if (!value) return { primary: "", secondary: "" };
    const [primary = "", secondary = ""] = value.split("|");
    return {
      primary: primary || "",
      secondary: secondary || "",
    };
  };

  const columns: ColumnDef<GetReportTemplatesResult>[] = useMemo(() => {
    const cols: ColumnDef<GetReportTemplatesResult>[] = [
      {
        id: "name",
        label: "Name",
        minWidth: 200,
        sortable: true,
        sticky: true,
        render: (template) => {
          const path =
            template.type === ReportTemplateTypeEnum.USER_REPORT
              ? "user-report"
              : template.type ===
                  ReportTemplateTypeEnum.ASSIGNMENT_ACIVITY_REPORT
                ? "assignment-activity-report"
                : "user-activity-report";

          return (
            <Box
              component="span"
              sx={{
                "& a": {
                  fontSize: 14,
                  color: brand[700],
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
              }}
            >
              <Link
                to={`${base}/reports/${path}?reportTemplateId=${template.ReportTemplateUid}`}
                state={{
                  template: template,
                  portalUid: portalUid,
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {template.name}
              </Link>
            </Box>
          );
        },
      },
      {
        id: "createdBy",
        label: "Created by",
        minWidth: 160,
        sortable: true,
        render: (template) => (
          <Stack>
            <Typography fontSize={14}>
              {template.createdBy.split("|")[0]}
            </Typography>
            <Typography color="#475467" fontSize={12}>
              {template.createdBy.split("|")[1]}
            </Typography>
          </Stack>
        ),
      },
      {
        id: "lastViewed",
        label: "Last viewed",
        minWidth: 160,
        sortable: true,
        render: (template) => {
          const formatted = formatLastViewedField(template.lastViewed);
          return (
            <Stack>
              <Typography fontSize={14}>{formatted.primary}</Typography>
              <Typography color="#475467" fontSize={12}>
                {formatted.secondary}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "lastUpdated",
        label: "Last updated",
        minWidth: 160,
        sortable: true,
        render: (template) => (
          <Stack>
            <Typography fontSize={14}>
              {template.lastUpdated.split("|")[0]}
            </Typography>
            <Typography color="#475467" fontSize={12}>
              {template.lastUpdated.split("|")[1]}
            </Typography>
          </Stack>
        ),
      },
      {
        id: "type",
        label: "Type",
        minWidth: 200,
        sortable: true,
        render: (template) => (
          <Typography sx={{ color: "#475467", fontSize: 14 }}>
            {template.type}
          </Typography>
        ),
      },
    ];

    if (isAdmin || isReportViewer) {
      cols.push({
        id: "actions",
        label: "",
        width: "100px",
        align: "center",
        render: (template) => (
          <Link
            to={base + "/reports/new-report"}
            state={template}
            style={{
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 14,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            Edit
          </Link>
        ),
      });
    }

    return cols;
  }, [isAdmin, isReportViewer, base, portalUid]);

  usePageMetadata(
    {
      title: "Reports",
      breadcrumbs: [
        {
          label: "Analytics",
          path: `/portal-configuration/${portalUid}/dashboard`,
        },
        {
          label: "Reports",
        },
      ],
    },
    [portalUid],
  );

  const actions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {anySelected && isAdmin && (
        <>
          <CustomActionIconLink
            title="Remove"
            icon={<DeleteOutlineIcon />}
            onClick={() => setConfirmDeletionModal(true)}
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}
      {isAdmin || isReportViewer ? (
        <StyledLink to={`${base}/reports/new-report`}>
          Add new report
        </StyledLink>
      ) : null}
    </Stack>
  );

  return (
    <>
      <DataTable
        data={templatesResp?.rows ?? []}
        columns={columns}
        totalCount={templatesResp?.count ?? 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sort={sort}
        onSortChange={handleSortChange}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name"
        actions={actions}
        selectedCountTitle="Selected templates"
        isLoading={loading}
        enableSelection={isAdmin}
        selectedRows={selectedTemplates}
        onSelectionChange={handleSelectionChange}
        getRowId={(template) => template.ReportTemplateUid}
      />

      <ConfirmModal
        openConfirmModal={confirmDeletionModal}
        setOpenConfirmModal={setConfirmDeletionModal}
        modalTitle="Remove selected reports"
        modalSubtitle="Are you sure you want to remove the selected reports?"
        confirmButtonClick={handleDeleteSelectedTemplates}
      />
    </>
  );
};
